"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBuyFormNFT = void 0;
var react_1 = require("react");
var _launchpadConstants_1 = require("../../../launchpadConstants");
var _pool_1 = require("../../../pool");
var hooks_1 = require("../../../pool/hooks");
var types_1 = require("../../../pool/types");
var _shared_1 = require("../../../shared");
var utils_1 = require("../../../shared/utils");
var useValidationNFT_1 = require("./useValidationNFT");
function useBuyFormNFT() {
    var _a = (0, _shared_1.useUser)(), account = _a.account, status = _a.kyc.status;
    var _b = (0, _pool_1.usePool)(), pool = _b.pool, saleInstance = _b.saleInstance, isPoolLoading = _b.isLoading, refreshPool = _b.refresh, isSoldOut = _b.isSoldOut;
    var sale = pool.sale, fundToken = pool.fundToken;
    var _c = (0, hooks_1.useUserPoolContributionNFT)(), userContribution = _c.contributionCurrency, userPoolBalance = _c.poolBalance, updateUserContribution = _c.update;
    var _d = (0, hooks_1.useUserCurrencyBalance)(), userCurrencyBalance = _d[0], updateCurrencyBalance = _d[1];
    var userState = (0, hooks_1.useUserSaleTier)().userState;
    var _e = (0, react_1.useState)('1'), tokenValue = _e[0], setTokenValue = _e[1];
    var _f = (0, react_1.useState)(sale.inventory[0]), selectedItem = _f[0], setSelectedItem = _f[1];
    var currencyValue = (0, utils_1.isValidValue)(tokenValue)
        ? (0, utils_1.toEth)(selectedItem.price.muln(parseInt(tokenValue || '0', 10)))
        : '0';
    var _g = (0, react_1.useState)(false), isLoading = _g[0], setIsLoading = _g[1];
    var validationErrors = (0, useValidationNFT_1.useValidationNFT)({
        currencyValue: currencyValue,
        tokenValue: tokenValue,
        userCurrencyBalance: userCurrencyBalance,
        tokensBalance: userPoolBalance,
        userCurrencyContribution: userContribution,
        tierAllocation: userState.levelAlloc,
        selectedItem: selectedItem,
    });
    var totalTokensLeft = sale.tokensForSale.sub(sale.tokensSold);
    var canBuy = userState.totalAlloc.gtn(0);
    // Keep the stats on active item updated
    (0, react_1.useEffect)(function () {
        setSelectedItem(sale.inventory.find(function (i) { return i.id === selectedItem.id; }));
    }, [sale.inventory]);
    function isMaxReached() {
        return userContribution.gte(userState.totalAlloc);
    }
    var isUserReachedLimit = isMaxReached();
    var userAllocationLeft = userState.totalAlloc.sub(userContribution);
    var isDisabled = isPoolLoading ||
        sale.status !== types_1.SaleStatus.open ||
        !account ||
        !saleInstance ||
        totalTokensLeft.eq(_launchpadConstants_1.ZERO_BN) ||
        tokenValue === '0' ||
        parseFloat(tokenValue) === 0 ||
        validationErrors.currencyError ||
        validationErrors.tokenError ||
        isUserReachedLimit ||
        (pool.kyc && status !== 'valid') ||
        !canBuy;
    /** Methods */
    function onTokenValueChange(e) {
        var value = String(parseInt((0, utils_1.normalize)(e.currentTarget.value), 10));
        setTokenValue(value);
    }
    function buyTokens() {
        var amount = parseInt(tokenValue, 10);
        if (!amount ||
            validationErrors.currencyError ||
            validationErrors.tokenError ||
            isLoading) {
            return;
        }
        setIsLoading(true);
        return (saleInstance.methods
            // @ts-expect-error not updated launchpad
            .buyTokens(selectedItem.internalId, amount)
            .send({
            from: account,
            gas: 420000,
            value: fundToken.address === '' ? selectedItem.price.muln(amount) : '0',
        })
            .then(function (res) {
            setTokenValue('0');
            updateUserContribution();
            updateCurrencyBalance();
            refreshPool();
            return {
                amount: tokenValue,
                transactionHash: res.transactionHash,
            };
        })
            .catch(function (err) {
            return {
                error: (err === null || err === void 0 ? void 0 : err.message) || err.toString(),
                msg: 'Failed to buy',
            };
        })
            .finally(function () {
            setIsLoading(false);
        }));
    }
    function getMaxAvailableTokens(forItem) {
        var item = forItem || selectedItem;
        var itemTokensLeft = item.supply - ((item === null || item === void 0 ? void 0 : item.sold) || 0);
        if (!itemTokensLeft) {
            return 0;
        }
        var currencyToTokens = function (v, price) {
            return Math.floor(v.mul(_launchpadConstants_1.DEC_NORM).div(price).div(_launchpadConstants_1.DEC_NORM).toNumber());
        };
        var maxTokensFromUserBalance = currencyToTokens(userCurrencyBalance, item.price);
        var maxTokensFromAllocation = currencyToTokens(userAllocationLeft, item.price);
        var userMin = Math.min(maxTokensFromAllocation, maxTokensFromUserBalance);
        var totalMin = Math.min(itemTokensLeft, userMin);
        if (item.limit) {
            return Math.min(totalMin, item.limit);
        }
        return totalMin;
    }
    function setMaxTokens() {
        var tokens = getMaxAvailableTokens();
        setTokenValue(String(tokens));
    }
    return {
        // general
        isLoading: isLoading,
        isDisabled: isDisabled,
        validationErrors: validationErrors,
        userWhitelisted: userState.isWhitelisted,
        isSoldOut: isSoldOut,
        userCurrencyBalance: userCurrencyBalance,
        userContribution: userContribution,
        userAllocationLeft: userAllocationLeft,
        userPoolBalance: userPoolBalance,
        isUserReachedLimit: isUserReachedLimit,
        // tier
        tierAllowsToBuy: userState.totalAlloc.gtn(0),
        // inputs
        currencyValue: currencyValue,
        tokenValue: tokenValue,
        selectedItem: selectedItem,
        onSelectedItemChange: function (item) {
            setTokenValue('1');
            setSelectedItem(item);
        },
        // functions
        onTokenValueChange: onTokenValueChange,
        getMaxAvailableTokens: getMaxAvailableTokens,
        setMaxTokens: setMaxTokens,
        buyTokens: buyTokens,
    };
}
exports.useBuyFormNFT = useBuyFormNFT;
